import type { SigningField, SnapshotPageSize } from '../types/fields';
import { isValidSnapshotField } from './signingFieldState';

export interface SignPdfTemplateBundle {
  version: 1;
  kind: 'signpdf-template';
  name: string;
  group?: string;
  documentName?: string;
  exportedAt: string;
  fields: SigningField[];
  /**
   * Page metadata at export time for proportional remap on import.
   * Entries are index-aligned with the source document's pages, so invalid
   * entries are preserved as `null` placeholders (rather than filtered out,
   * which would shift indices and silently drop fields on later pages).
   */
  pageSizes?: (SnapshotPageSize | null)[];
}

export function buildTemplateBundle(params: {
  name: string;
  fields: SigningField[];
  group?: string;
  documentName?: string;
  pageSizes?: SnapshotPageSize[];
}): SignPdfTemplateBundle {
  return {
    version: 1,
    kind: 'signpdf-template',
    name: params.name,
    group: params.group,
    documentName: params.documentName,
    exportedAt: new Date().toISOString(),
    fields: structuredClone(params.fields),
    pageSizes: params.pageSizes ? structuredClone(params.pageSizes) : undefined,
  };
}

export function downloadTemplateBundle(bundle: SignPdfTemplateBundle): void {
  const safe = bundle.name.replace(/[^a-z0-9._-]+/gi, '-').slice(0, 60) || 'template';
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${safe}.signpdf-template.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export function parseTemplateBundle(raw: unknown): SignPdfTemplateBundle {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid template file.');
  }
  const obj = raw as Partial<SignPdfTemplateBundle>;
  if (obj.kind !== 'signpdf-template' || obj.version !== 1) {
    throw new Error('Unrecognized template format. Expected .signpdf-template.json v1.');
  }
  if (!obj.name || typeof obj.name !== 'string') {
    throw new Error('Template is missing a name.');
  }
  if (!Array.isArray(obj.fields) || obj.fields.length === 0) {
    throw new Error('Template has no fields.');
  }
  const fields = obj.fields.filter(isValidSnapshotField) as SigningField[];
  if (fields.length === 0) {
    throw new Error('Template has no valid fields.');
  }
  // Preserve array indices: map invalid entries to null rather than filtering
  // them out. Filtering compacts the array, which shifts the indices that
  // field.pageIndex references and silently drops fields on later pages during
  // restore (F59/F60).
  const pageSizes = Array.isArray(obj.pageSizes)
    ? (obj.pageSizes as (SnapshotPageSize | null)[]).map((p) =>
        p &&
        Number.isFinite(p.width) &&
        Number.isFinite(p.height) &&
        p.width > 0 &&
        p.height > 0
          ? p
          : null
      )
    : undefined;
  return {
    version: 1,
    kind: 'signpdf-template',
    name: obj.name.trim().slice(0, 100),
    group: typeof obj.group === 'string' ? obj.group : undefined,
    documentName: typeof obj.documentName === 'string' ? obj.documentName : undefined,
    exportedAt: typeof obj.exportedAt === 'string' ? obj.exportedAt : new Date().toISOString(),
    fields,
    // Only retain page sizes if at least one entry is valid; an all-null array
    // carries no remap information.
    pageSizes: pageSizes && pageSizes.some((p) => p !== null) ? pageSizes : undefined,
  };
}